<template>
  <div class="form-wrap">
    <div class="form-content">
      <component
        :is="FormRender3Async"
        :form="field.form"
        v-model:model="value"
        :options="field.form?.options"
        :validateInfos="validateInfos"
        @update_model="onUpdateModel"
        :alwaysShowGroup="alwaysShowGroup"
        :is_form_design="is_form_design"
        :inner-model="innerModel"
        :global-inner-model="globalInnerModel"
        :modalTypeList="modalTypeList"
        :context="context"
        :modalType="modalType"
        :base-key-path="baseKeyPath"
        :worker-host-functions="workerHostFunctions"
        :lock-structure="lockStructure"
      />
    </div>
    <div
      v-if="field.max_count !== min_count && !lockStructure"
      class="form-action"
    >
      <a-button
        @click="copyFormItem"
        size="small"
        type="link"
        :disabled="field.max_count && listLength >= field.max_count"
      >
        <template #icon>
          <CopyOutlined />
        </template>
      </a-button>
      <a-button
        @click="removeFormItem"
        size="small"
        type="link"
        danger
        :disabled="listLength <= min_count"
      >
        <template #icon>
          <DeleteOutlined />
        </template>
      </a-button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineAsyncComponent,
  defineComponent,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  PropType,
  watch,
} from 'vue';
import { useForm } from 'ant-design-vue/es/form';

export default defineComponent({
  name: 'FormTemplate',
  components: {},
  props: {
    field: {
      type: Object,
      required: true,
    },
    modalType: {
      type: String,
      required: true,
      default: () => '',
    },
    is_form_design: {
      type: Boolean,
      default: false,
    },
    alwaysShowGroup: {
      type: Boolean,
      default: false,
    },
    innerModel: {
      default: null,
    },
    globalInnerModel: {
      default: null,
    },
    modalTypeList: {
      type: Array as PropType<any[]>,
      default: null,
    },

    nestValidate: {
      type: Object as PropType<Record<string, any> | null>,
      default: null,
    },
    value: {
      type: Object,
      default: null,
    },
    min_count: {
      type: Number,
      default: 1,
    },
    listLength: {
      type: Number,
      default: 0,
    },
    context: {
      type: Object,
      default: null,
    },
    baseKeyPath: {
      type: Array,
      default: () => [],
    },
    workerHostFunctions: {
      type: Object,
      default: () => {},
    },
    // 修改模式传入 true：隐藏行删除按钮，禁止增删行
    lockStructure: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value', 'remove', 'copy'],
  setup(props, { emit }) {
    const rulesRef = reactive({ ...props.field.form.rules });
    const validateInfos = ref({});
    const FormRender3Async = defineAsyncComponent(
      () => import('liyu-pc-base/components/FormRender/FormRender3.vue'),
    );
    let validate;
    const genID = (): string => {
      const array = new Uint8Array(16); // 16 字节 = 128 位
      window.crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    };
    // 记录需要忽略校验的字段
    const removeRulKeys = ref([]);
    const valite_id = genID();
    const syncValidate = ([_modelRef, _removeRulKeys]: any[]) => {
      const new_rules_obj = {};
      Object.keys(rulesRef).forEach(key => {
        if (key in _modelRef && !_removeRulKeys[key]) {
          new_rules_obj[key] = rulesRef[key];
        }
      });
      const newRes: any = useForm(_modelRef, new_rules_obj);
      validate = newRes.validate;
      validateInfos.value = newRes.validateInfos;
      if (props.nestValidate) {
        props.nestValidate[valite_id] = validate;
      }
    };
    const onUpdateModel = async params => {
      if (params.add_key) {
        // 添加字段时，不忽略校验，所以需要从removeRulKeys中删除
        delete removeRulKeys.value[params.add_key];
        syncValidate([props.value, removeRulKeys.value]);
      } else if (params.remove_key) {
        // 删除字段时，忽略校验，所以需要添加到removeRulKeys中
        removeRulKeys.value[params.remove_key] = true;
        syncValidate([props.value, removeRulKeys.value]);
      }
    };
    watch(
      () => props.value,
      newValue => {
        syncValidate([newValue, removeRulKeys.value]);
      },
      { deep: true, immediate: true },
    );

    const removeFormItem = () => {
      if (props.nestValidate) {
        delete props.nestValidate[valite_id];
      }
      emit('remove');
    };
    // 复制行：由父级 FormTemplate 负责插入到当前行下一行
    const copyFormItem = () => {
      emit('copy');
    };
    onMounted(() => {});
    onUnmounted(() => {
      if (props.nestValidate) {
        delete props.nestValidate[valite_id];
      }
    });
    return {
      FormRender3Async,
      validateInfos,
      onUpdateModel,
      removeFormItem,
      copyFormItem,
    };
  },

  data() {
    return {
      model: this.value,
    };
  },
});
</script>

<style lang="less" scoped>
.top_btn {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 8px;
}
.form-wrap {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 4px;
  margin-bottom: 4px;
  width: 100%;
  .form-content {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }
  .form-action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding-top: 8px;
    flex-shrink: 0;
  }
}
</style>
